import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Alert, Snackbar, Paper, Typography, Button, TableContainer, Table, TableRow, TableHead, TableCell, TableBody, IconButton, Box, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useMediaQuery } from "@mui/material";
import { createTheme } from '@mui/material/styles';
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import DownloadIcon from '@mui/icons-material/GetApp';
import IAlert from "../../types/Alert";
import IInvoice from "../../types/Invoice";
import Utilities from "../../common/Utilities";
import IUser from "../../types/User";
import Config from "../../utils/Config";
import AuthService from "../../services/AuthService";
import * as constantes from "../../common/Constantes";
import StripeService from "../../services/StripeService";
import stripePromise, { stripeElementsOptions } from "../../common/Stripe";
import { Elements } from "@stripe/react-stripe-js";
import PaymentMethodForm, { UserPaymentMethodData } from "./PaymentMethodForm";
import PaymentFailureNotice from "./PaymentFailureNotice";
import BillingDataForm, { UserBillingData } from "./BillingDataForm";
import PlanChangeForm from "./PlanChangeForm";
import ConfigUtils from "../../utils/Config";
import { IPlan } from "../../types/Plan";

const allowedTesterEmails = import.meta.env.REACT_APP_ALLOWED_TESTER_EMAILS?.split(',') || [];

const Suscripcion = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState<IUser>();
    const [invoices, setInvoices] = useState<IInvoice[]>([]);
    const [plan, setPlan] = useState<{ name: string, amount: number }>();
    const [planes, setPlanes] = useState<IPlan[]>([]);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const [modalCancel, setModalCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const [modalPago, setModalPago] = useState(false);
    const [modalSuscripcion, setModalSuscripcion] = useState(false);
    const [modalPlanes, setModalPlanes] = useState(false);

    const userId = Config.getUserId();
    const userRole: number = ConfigUtils.getUserRole();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const getBillingAndPlanes = async () => {
      try {
        setLoading(true);
        const [billingResponse, plansResponse] = await Promise.all([
          AuthService.getBillingData(userId),
          StripeService.getAvailablePlans()
        ]);
         // Datos de facturación
        setUser(billingResponse.data.user);
        setInvoices(billingResponse.data.invoices);
        setPlan(billingResponse.data.plan);
        const userEmail = Config.getUserEmail();
        const isTesterEligible = allowedTesterEmails.includes(userEmail);
        const availablePlans = plansResponse.data
          .filter((p: IPlan) => {
            if (p.name.includes('Tester') && !isTesterEligible) return false;
            if (p.name === billingResponse.data.plan.name) return false;
            return true;
          })
          .sort((a: IPlan, b: IPlan) => a.amount - b.amount);
        
        setPlanes(availablePlans);
      } catch (e: any) { 
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = { method: 'Suscripcion.Component.getBillingAndPlanes', level: 'error', message: e.message, meta: e.response };
          LogService.logError(error);
          setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || e.message });
        } 
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      getBillingAndPlanes();
    }, []);

    const handleDownload = (invoiceId: string, invoiceNumber: string) => {
      setDownloadingId(invoiceId);
      StripeService.downloadInvoice(invoiceId)
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Invoice-${invoiceNumber}`;

        link.click();

        URL.revokeObjectURL(link.href);
      })
      .catch((e: any) => {
        if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'Suscripcion.Component.handleDownload', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
      })
      .finally(() => {
        setDownloadingId(null);
      });
    };

    const renderInvoiceStatus = (status: string) => {
      switch (status) {
        case "paid":
            return <Chip label="Pagada" color="success" />;
        case "open":
            return <Chip label="Pendiente" color="warning" />;
        case "uncollectible":
            return <Chip label="Fallida" color="error" />;
        case "void":
            return <Chip label="Anulada" color="default" />;
        default:
            return <Chip label={`Desconocido: ${status}`} color="info" />;
      }
    };

    const cancelSubscription = () => {
      setCancelling(true);
      setModalCancel(false);

      StripeService.cancelSubscription()
      .then((response) => {
        setAlert({ open: true, severity: 'success', msg: response.data.message });
      })
      .catch((e: any) => {
        if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'Suscripcion.Component.cancelSubscription', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
      })
      .finally(() => {
        setCancelling(false);
        getBillingAndPlanes();
      });
    };

    const reactivateSubscription = () => {
      setCancelling(true);
      StripeService.reactivateSubscription()
      .then((response) => {
        setAlert({ open: true, severity: 'success', msg: response.data.message });
      })
      .catch((e: any) => {
        if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'Suscripcion.Component.reactivateSubscription', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
      })
      .finally(() => {
        setCancelling(false);
        getBillingAndPlanes();
      });
    };

    const updatePaymentStateData = (newPaymentMethod: UserPaymentMethodData) => {
      setUser(prevUser => {
        if (!prevUser) return prevUser;

        const updatedSubscription = prevUser.subscription?.status === 'payment_failed'
          ? { ...prevUser.subscription, status: 'active', paymentFailedReason: null }
          : prevUser.subscription;

        return {
          ...prevUser,
          paymentMethod: {
            ...(prevUser.paymentMethod || {}),
            ...newPaymentMethod
          },
          subscription: updatedSubscription
        };
      });

      setInvoices(prevInvoices => {
        const sorted = [...prevInvoices].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const updated = sorted.map((invoice, index) => {
          if (index === 0 && invoice.status === 'open') {
            return { ...invoice, status: 'paid' };
          }
          return invoice;
        });
        return updated;
      });
    };

    // Cobro rechazado: la tarjeta nueva ya es la del cliente, pero la suscripción y la factura siguen igual
    const updateCardStateData = (newPaymentMethod: UserPaymentMethodData, newFailureReason?: string) => {
      setUser(prevUser => prevUser && ({
        ...prevUser,
        paymentMethod: { ...(prevUser.paymentMethod || {}), ...newPaymentMethod },
        subscription: newFailureReason && prevUser.subscription
          ? { ...prevUser.subscription, paymentFailedReason: newFailureReason }
          : prevUser.subscription
      }));
    };

    // Un reintento rechazado puede dejar un motivo distinto al que figuraba
    const updateFailureReason = (newFailureReason: string) => {
      setUser(prevUser => prevUser && prevUser.subscription
        ? { ...prevUser, subscription: { ...prevUser.subscription, paymentFailedReason: newFailureReason } }
        : prevUser);
    };

    const updateBillingStateData = (newBilling: UserBillingData) => {
      setUser(prevUser => {
        if (!prevUser) return prevUser;

        return {
          ...prevUser,
          datosFacturacion: {
            ...(prevUser.datosFacturacion || {}),
            ...newBilling
          },
        };
      });
    };

    const paymentFailed = user?.subscription?.status === 'payment_failed';

    try {
        return (
          userRole == constantes.ROLES.ADMINISTRADOR ?
            <Grid sx={{ marginX: 2 }}>
              <Paper elevation={3} sx={{ marginTop: 2, marginBottom: 3, padding: 3, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h4" sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1), fontWeight: 'bold' }}>SUSCRIPCIÓN</Typography>
                  {loading ? <CircularProgress /> : <Grid container spacing={3}>
                      <Grid item lg={3} md={12} xs={12}>
                          <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', position: 'relative' }}>
                              <Button 
                                disabled={paymentFailed || user?.subscription?.status === 'pending_plan_upgrade' || user?.subscription?.status === 'pending_plan_downgrade'} //se deshabilita el cambio de plan
                                variant="outlined" 
                                sx={{ 
                                  position: 'absolute', 
                                  top: 10, 
                                  right: 10, 
                                  padding: '4px 8px', 
                                  fontSize: '0.75rem',
                                  borderColor: 'text.secondary',
                                  color: 'text.secondary',
                                  '&:hover': {
                                    borderColor: 'text.primary',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                  }
                                }}
                                onClick={() => setModalPlanes(true)}
                              >
                              Editar
                            </Button>
                              
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Detalles del plan</Typography>
                              <Typography variant="body1">{plan?.name}</Typography>
                              <Typography variant="body1">{`${Utilities.formatAmount(plan?.amount ?? 0)}€ / mes`}</Typography>
                          </Paper>
                      </Grid>
                      <Grid item lg={3} md={12} xs={12}>
                          <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', position: 'relative', borderLeft: paymentFailed ? `4px solid ${theme.palette.error.main}` : 'none' }}>
                            {paymentFailed && userId ?
                              <>
                                <Elements stripe={stripePromise} options={stripeElementsOptions}>
                                  <PaymentFailureNotice
                                    userId={userId}
                                    reason={user?.subscription?.paymentFailedReason}
                                    withSession
                                    onSuccess={(newPaymentMethod) => {
                                      setAlert({ open: true, severity: 'success', msg: 'Pago completado correctamente' });
                                      updatePaymentStateData(newPaymentMethod);
                                    }}
                                    onUpdateCard={() => setModalPago(true)}
                                    onReasonChange={updateFailureReason}
                                  />
                                </Elements>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{`Método de pago: tarjeta terminada en ${user?.paymentMethod?.cardLast4} · expira ${user?.paymentMethod?.cardExpMonth}/${user?.paymentMethod?.cardExpYear}`}</Typography>
                              </> :
                              <>
                                <Button 
                                  variant="outlined" 
                                  sx={{ 
                                    position: 'absolute', 
                                    top: 10, 
                                    right: 10, 
                                    padding: '4px 8px', 
                                    fontSize: '0.75rem',
                                    borderColor: 'text.secondary',
                                    color: 'text.secondary',
                                    '&:hover': {
                                      borderColor: 'text.primary',
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                  }}
                                  onClick={() => setModalPago(true)}
                                >
                                  Editar
                                </Button>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Método de pago</Typography>
                                <Typography variant="body1">{`Termina en ${user?.paymentMethod?.cardLast4}`}</Typography>
                                <Typography variant="body1">{`Expira ${user?.paymentMethod?.cardExpMonth}/${user?.paymentMethod?.cardExpYear}`}</Typography>
                              </>
                            }
                          </Paper>
                      </Grid>
                      <Grid item lg={6} md={12} xs={12}>
                          <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column', position: 'relative' }}>
                            <Button 
                              disabled={paymentFailed}
                              variant="outlined" 
                              sx={{ 
                                position: 'absolute', 
                                top: 10, 
                                right: 10, 
                                padding: '4px 8px', 
                                fontSize: '0.75rem',
                                borderColor: 'text.secondary',
                                color: 'text.secondary',
                                '&:hover': {
                                  borderColor: 'text.primary',
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                              onClick={() => setModalSuscripcion(true)}
                            >
                              Editar
                            </Button>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Datos de facturación</Typography>
                              <Typography variant="body1">{`${user?.datosFacturacion?.nombre} - ${user?.datosFacturacion?.nif}`}</Typography>
                              <Typography variant="body1">{`${user?.datosFacturacion?.calle} ${user?.datosFacturacion?.codigoPostal} ${user?.datosFacturacion?.ciudad} - ${constantes.paises.find(p => p.id === user?.datosFacturacion?.pais)?.name}`}</Typography>
                          </Paper>
                      </Grid>
                      <Grid item lg={12} md={12} xs={12}>
                          <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Historial de facturas</Typography>
                              {invoices.length  === 0 ? <Typography variant="body1">No se han encontrado facturas</Typography> : 
                              isMobile ? 
                              <Box>
                                {invoices.map((invoice: IInvoice) => (
                                  <Paper
                                    key={invoice.invoiceId}
                                    sx={{
                                      p: 2,
                                      mb: 2,
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="body2" color="text.secondary">
                                        {Utilities.formatMonthYear(invoice.date.toString())}
                                      </Typography>

                                      <Chip
                                        size="small"
                                        label={invoice.type === 'subscription' ? 'Suscripción' : 'Pack de mensajes'}
                                        color={invoice.type === 'subscription' ? 'primary' : 'secondary'}
                                        sx={{ mt: 0.5 }}
                                      />
                                  
                                      {renderInvoiceStatus(invoice.status)}
                                    </Box>

                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      mt={1.5}
                                    >
                                      <Typography variant="h6" fontWeight={600}>
                                        {Utilities.formatAmount(invoice.amount / 100)} €
                                      </Typography>
                                  
                                      <IconButton
                                        onClick={() => handleDownload(invoice.invoiceId, invoice.number)}
                                        disabled={downloadingId === invoice.invoiceId}
                                        size="small"
                                      >
                                        {downloadingId === invoice.invoiceId ? (
                                          <CircularProgress size={18} />
                                        ) : (
                                          <DownloadIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Box>
                                  </Paper>
                                ))}
                              </Box> : 
                              <TableContainer>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>FECHA</TableCell>
                                      <TableCell>CONCEPTO</TableCell>
                                      <TableCell>ESTADO</TableCell>
                                      <TableCell align="right" colSpan={2} sx={{ paddingRight: 8 }}>IMPORTE</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {invoices.map((invoice: IInvoice) => (
                                      <TableRow key={invoice.invoiceId}>
                                        <TableCell>{Utilities.formatMonthYear(invoice.date.toString())}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={
                                              invoice.type === 'subscription'
                                                ? 'Suscripción'
                                                : 'Pack de mensajes'
                                            }
                                            color={
                                              invoice.type === 'subscription'
                                                ? 'primary'
                                                : 'secondary'
                                            }
                                          />
                                        </TableCell>
                                        <TableCell>{renderInvoiceStatus(invoice.status)}</TableCell>
                                        <TableCell align="right" colSpan={2}>
                                          <Box display="flex" justifyContent="flex-end" alignItems="center">
                                              <span style={{ marginRight: '8px' }}>{`${Utilities.formatAmount(invoice.amount/100)} €`}</span>
                                              <IconButton onClick={() => handleDownload(invoice.invoiceId, invoice.number)} disabled={downloadingId === invoice.invoiceId} >
                                                {downloadingId === invoice.invoiceId ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <DownloadIcon />
                                                )}
                                              </IconButton>
                                          </Box>
                                      </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>}
                          </Paper>
                      </Grid>
                      { user?.subscription?.status === 'pending_cancellation' && 
                        <Box>
                          {user.subscription.endDate && <Typography sx={{ paddingLeft: theme.spacing(3), paddingTop: theme.spacing(3) }} variant="body1">{`Suscripción activa hasta el ${new Date(user.subscription.endDate).getDate()} de ${constantes.monthNames[new Date(user.subscription.endDate).getMonth()]}`}</Typography>}
                          <Button variant="contained" color="error" sx={{ margin: 3 }} disabled={paymentFailed || cancelling} onClick={reactivateSubscription}>{cancelling ? <CircularProgress size={24} color="inherit" /> : 'REACTIVAR SUSCRIPCIÓN'}</Button>
                        </Box>
                      }
                      { user?.subscription?.status === 'active' && <Button variant="contained" color="error" sx={{ margin: 3 }} disabled={paymentFailed || cancelling} onClick={() => setModalCancel(true)}>{cancelling ? <CircularProgress size={24} color="inherit" /> : 'CANCELAR SUSCRIPCIÓN'}</Button>}
                  </Grid>}
              </Paper>

              {/* MODAL EDICIÓN DE PLAN */}
              <Dialog open={modalPlanes} onClose={() => setModalPlanes(false)} maxWidth="md" fullWidth >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', mt: 1, }} >
                  Cambiar Plan de Suscripción
                </DialogTitle>
                
                <DialogContent>
                  <Elements stripe={stripePromise} options={stripeElementsOptions}>
                  <PlanChangeForm
                    planes={planes}
                    closeModal={() => setModalPlanes(false)}
                    onSuccess={() => {getBillingAndPlanes(); setAlert({ open: true, severity: 'success', msg: 'Tu plan se ha actualizado correctamente' });}}
                  />
                  </Elements>
                </DialogContent>
              </Dialog>

              {/* CONFIRMACIÓN CANCELACIÓN SUSCRIPCIÓN */}
                <Dialog open={modalCancel} maxWidth="lg" onClose={() => setModalCancel(false)}>
                    <DialogTitle>CANCELAR SUSCRIPCIÓN</DialogTitle>
                    <DialogContent>
                        <DialogContentText>¿Está seguro de que desea cancelar la suscripción?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelSubscription} color="primary">SI</Button>
                        <Button onClick={() => setModalCancel(false)} color="primary" autoFocus>NO</Button>
                    </DialogActions>
                </Dialog>

              {/* MODAL EDICIÓN DATOS DE PAGO */}
                <Dialog open={modalPago} onClose={() => setModalPago(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 3, boxShadow: 5 }}}>
                  <DialogTitle
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.4rem',
                      pb: 0,
                    }}
                  >
                    Actualizar método de pago
                  </DialogTitle>
                  <DialogContent sx={{ mt: 2 }}>
                    <Elements stripe={stripePromise} options={stripeElementsOptions}>
                      <PaymentMethodForm 
                        userId={userId} 
                        paymentFailed={paymentFailed}
                        onSuccess={(newPaymentMethod) => {
                          setAlert({ open: true, severity: 'success', msg: 'Método de pago actualizado correctamente' });
                          setModalPago(false);
                          updatePaymentStateData(newPaymentMethod);
                        }}
                        onCardUpdated={updateCardStateData}
                      />
                    </Elements>
                  </DialogContent>
                </Dialog>

              {/* MODAL EDICIÓN DATOS SUSCRIPCIÓN */}
                <Dialog open={modalSuscripcion} onClose={() => setModalSuscripcion(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 3, boxShadow: 5 }}}>
                  <DialogTitle
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.4rem',
                      pb: 0,
                    }}
                  >
                    Editar Datos de Facturación
                  </DialogTitle>
                  <DialogContent sx={{ mt: 2 }}>
                    {user?.datosFacturacion && <BillingDataForm 
                      userId={userId}
                      onSuccess={(newBilling, caso) => {
                        setAlert({ open: true, severity: 'success', msg: caso === 'intercambio'
                          ? 'Entidad principal cambiada correctamente: cada entidad conserva sus facturas y su numeración'
                          : caso === 'archivo'
                            ? 'Datos de facturación actualizados: la entidad anterior se ha guardado desactivada con sus facturas'
                            : 'Datos de facturación actualizados correctamente' });
                        setModalSuscripcion(false);
                        updateBillingStateData(newBilling);
                      }} 
                      initialData={user.datosFacturacion}
                    />}
                  </DialogContent>
                </Dialog>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                  <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </Grid>
            :
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open>
                <Alert severity="warning" sx={{ width: '100%' }} >Usuario no autorizado</Alert>
            </Snackbar>
        );
    } catch (e: any) {
        const error: ILog = { method: 'Suscripcion.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default Suscripcion;